import { useEffect, useMemo, useRef, useState } from 'react';
import { useNotifications, useMarkNotificationsRead } from '../lib/queries';
import {
  isPushSupported,
  getCurrentPushPermission,
  getCurrentSubscription,
  subscribeToPush,
  unsubscribeFromPush,
} from '../lib/push';

export function NotificationsBell() {
  const { data: items = [] } = useNotifications();
  const markRead = useMarkNotificationsRead();
  const [open, setOpen] = useState(false);
  const [pushOn, setPushOn] = useState<boolean | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [busy, setBusy] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const unread = useMemo(() => items.filter((n) => !n.readAt), [items]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!isPushSupported()) return;
      setPermission(await getCurrentPushPermission());
      const sub = await getCurrentSubscription();
      if (active) setPushOn(!!sub);
    })();
    return () => {
      active = false;
    };
  }, []);

  // Click outside or press Escape closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const togglePush = async (): Promise<void> => {
    setBusy(true);
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
      } else {
        await subscribeToPush();
        setPushOn(true);
      }
      setPermission(await getCurrentPushPermission());
    } catch (err) {
      console.error('Push toggle failed', err);
      alert(err instanceof Error ? err.message : 'Could not change push state.');
    } finally {
      setBusy(false);
    }
  };

  const onItemClick = (id: string, url?: string): void => {
    markRead.mutate([id]);
    if (url) window.location.href = url;
  };

  const markAllRead = (): void => {
    if (unread.length === 0) return;
    markRead.mutate(unread.map((n) => n.id));
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-white transition hover:bg-white/15"
        aria-label="Notifications"
        aria-expanded={open}
        title={unread.length > 0 ? `${unread.length} unread notification${unread.length === 1 ? '' : 's'}` : 'Notifications'}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-5 w-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7c0-3.314-2.686-6-6-6s-6 2.686-6 6v.7a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m7.713 0a24.255 24.255 0 01-7.713 0m7.713 0a3 3 0 11-7.713 0" />
        </svg>
        {unread.length > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-80 max-w-[calc(100vw-1rem)] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
            <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
            <button
              type="button"
              onClick={markAllRead}
              disabled={unread.length === 0 || markRead.isPending}
              className="text-xs font-medium text-blue-600 hover:underline disabled:opacity-40"
            >
              Mark all read
            </button>
          </div>
          {isPushSupported() && (
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <span className="text-slate-700">
                Push: <span className="font-medium">{pushOn ? 'on' : permission === 'denied' ? 'blocked' : 'off'}</span>
              </span>
              <button
                type="button"
                onClick={togglePush}
                disabled={busy || permission === 'denied'}
                className="rounded-md bg-blue-600 px-2 py-1 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {pushOn ? 'Disable' : 'Enable'}
              </button>
            </div>
          )}
          <ul className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <li className="px-3 py-8 text-center text-sm text-slate-500">You're all caught up.</li>
            )}
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => onItemClick(n.id, n.url)}
                  className={`block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 ${n.readAt ? 'text-slate-600' : 'bg-blue-50/40 text-slate-900'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium">{n.title}</span>
                    {!n.readAt && <span className="mt-1 inline-block h-2 w-2 flex-none rounded-full bg-blue-500" />}
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-xs text-slate-600">{n.body}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">
                    {new Date(n.createdAt).toLocaleString()}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
